import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Send, Brain, Sparkles, MessageSquare, Plus, Settings, User, Users, Lock, LogIn, LogOut, FolderOpen, Archive, ArrowLeft, BarChart3, Scale, Shield, Share2, Database, TrendingUp, Wallet, Server, FileText, Headphones, Wand2, ScrollText, Mail, CreditCard, GraduationCap, Network, Terminal, CalendarDays } from 'lucide-react';
import { useTheme } from './contexts/ThemeContext';

// Import des composants équipe
import BenDashboard from './components/Dashboard/BenDashboard';
import PierreDashboard from './components/Dashboard/PierreDashboard';
import AnnieDashboard from './components/Dashboard/AnnieDashboard';
import ProjectsList from './components/Projects/ProjectsList';
import ProjectDetail from './components/Projects/ProjectDetail';
import RecentSessionsSubmenu from './components/navigation/RecentSessionsSubmenu';
import ProjectsTreeSubmenu from './components/navigation/ProjectsTreeSubmenu';
import BasesSubmenu from './components/navigation/BasesSubmenu';
import WelcomeGregModal from './components/onboarding/WelcomeGregModal';
import ForcePasswordChangeModal from './components/auth/ForcePasswordChangeModal';
import LoginPage from './components/auth/LoginPage';
import ProfileDashboard from './components/profile/ProfileDashboard';
import SidebarChronicles from './components/chronicles/SidebarChronicles';

// Import nouvelle navigation
import AppLayout from './components/navigation/AppLayout';
import NavSection from './components/navigation/NavSection';
import ToggleCM from './components/ui/ToggleCM';
import { ToastContainer, toast, confirm as confirmModal } from './components/ui/Toast';
import { VaultZkProvider } from './components/vault/VaultZkContext';
import SuspensionNoticeOverlay from './components/ui/SuspensionNoticeOverlay';
import useSuspensionPoll from './hooks/useSuspensionPoll';
import DecodexMenu from './components/decodex/DecodexMenu';
import FilesSidebar from './components/files/FilesSidebar';
import PasteAsFileWidget from './components/files/PasteAsFileWidget';
import InputToolbar from './components/toolbar/InputToolbar';
import UserManagement from './components/Admin/UserManagement';
import ModelCatalogAdmin from './components/Admin/ModelCatalogAdmin';
import UserProfile from './components/Auth/UserProfile';
import VaultPage from './components/vault/VaultPage';
import BookingPage from './components/booking/BookingPage';
import MailingPage from './components/mailing/MailingPage';
import DocumentsPage from './components/documents/DocumentsPage';
import DevCockpitPage from './components/dev/DevCockpitPage';
import ProjectArchives from './components/Projects/ProjectArchives';
import CostDashboard from './components/analytics/CostDashboard';
import ProjectMetrics from './components/analytics/ProjectMetrics';
import InfrastructureDashboard from './components/Admin/InfrastructureDashboard';
import LogsDashboard from './components/Admin/LogsDashboard';
import DataMemoryMap from './components/Admin/DataMemoryMap';
import DialecteBench from './components/Admin/DialecteBench';
import ChroniclesAdmin from './components/Admin/ChroniclesAdmin';
import SplitChatView from './components/split/SplitChatView';
import usePreviewPane from './hooks/usePreviewPane';

// Import composants RH (Rendement Humain)
import RHDashboard from './components/RH/RHDashboard';
import RHStudyForm from './components/RH/RHStudyForm';
import RHStudyDetail from './components/RH/RHStudyDetail';
import RHCausalGraph from './components/RH/RHCausalGraph';

// Import composants conversations + profil
import ConversationList from './components/conversations/ConversationList';
import ConversationChat from './components/conversations/ConversationChat';
import WelcomeScreen from './components/conversations/WelcomeScreen';
import ProfileIndicator from './components/profile/ProfileIndicator';
import ProfileSummary from './components/profile/ProfileSummary';
import CommKnowledgeAdmin from './components/comm/CommKnowledgeAdmin';
import SpeechStudio from './components/speech/SpeechStudio';
import PricingPage from './components/pricing/PricingPage';
import FeatureLock from './components/pricing/FeatureLock';
import { getQuota } from './services/quotaApi';
import ExpertiseBasesPanel from './components/expertise/ExpertiseBasesPanel';
import LmsPanel from './components/lms/LmsPanel';

const API_URL = '/api';

// Intercepteur 401 — déconnexion auto uniquement si le token ITRIIA est rejeté
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Seulement les appels API ITRIIA (pas les streams, pas les services externes)
      const isItriiaApi = url.startsWith('/api/') && !url.includes('/stream');
      const errMsg = error.response?.data?.error || '';
      const isAuthError = errMsg.includes('Token') || errMsg.includes('Session expirée') || errMsg.includes('Utilisateur non trouvé');
      if (isItriiaApi && isAuthError) {
        localStorage.removeItem('oracle_token');
        localStorage.removeItem('oracle_user');
        delete axios.defaults.headers.common['Authorization'];
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

// Store simple pour conversations
const useOracleStore = () => {
  const [conversations, setConversations] = useState([
    {
      id: 'default',
      title: 'Oracle Conversations',
      messages: []
    }
  ]);

  const [currentConversationId, setCurrentConversationId] = useState('default');

  const getCurrentConversation = () => {
    return conversations.find(c => c.id === currentConversationId) || conversations[0];
  };

  const addMessage = (message) => {
    setConversations(prev => prev.map(conv =>
      conv.id === currentConversationId
        ? { ...conv, messages: [...conv.messages, message] }
        : conv
    ));
  };

  const updateLastMessage = (updates) => {
    setConversations(prev => prev.map(conv =>
      conv.id === currentConversationId
        ? {
            ...conv,
            messages: conv.messages.map((msg, index) =>
              index === conv.messages.length - 1 ? { ...msg, ...updates } : msg
            )
          }
        : conv
    ));
  };

  return {
    conversations,
    currentConversationId,
    setCurrentConversationId,
    getCurrentConversation,
    addMessage,
    updateLastMessage,
    setConversations
  };
};

// Panel minimal « formation » (onboarding devs ITRIIA International) :
// masque Studio / Coffre-fort / Mailing / Mon abonnement / Analytics.
// Restent visibles : Mon Espace, Formations (si permission lms), Projets.
const isFormationPanel = (u) => u?.role === 'formation';

function App() {
  const [user, setUser] = useState(null);
  // Chantier #5 — palier / quotas (source : GET /api/quota/me)
  const [quota, setQuota] = useState(null);
  const refreshQuota = useCallback(async () => {
    try { setQuota(await getQuota()); } catch { /* silencieux */ }
  }, []);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  // Restauration état depuis le hash URL
  const parseHash = () => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return {};
    const [page, ...rest] = hash.split('/');
    return { page, param: rest.join('/') || null };
  };
  const initialHash = parseHash();

  const [currentPage, setCurrentPage] = useState(initialHash.page || 'conversations');
  // Sidebar : sections rétractées par défaut, mais l'état persiste entre sessions (Session Greg)
  const [expandedMenu, setExpandedMenu] = useState(() => {
    try { return localStorage.getItem('oracle_expanded_menu') || null; } catch { return null; }
  });
  useEffect(() => {
    try {
      if (expandedMenu) localStorage.setItem('oracle_expanded_menu', expandedMenu);
      else localStorage.removeItem('oracle_expanded_menu');
    } catch { /* silent */ }
  }, [expandedMenu]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [pendingProjectId, setPendingProjectId] = useState(initialHash.page === 'projects' && initialHash.param ? parseInt(initialHash.param) : null);
  const [pendingSessionId, setPendingSessionId] = useState(null);
  const [pendingBaseId, setPendingBaseId] = useState(null);
  const [expertiseInitialView, setExpertiseInitialView] = useState(null);
  const [focusMode, setFocusMode] = useState(false);
  const [filesRefreshKey, setFilesRefreshKey] = useState(0);
  const [fileAnalyzeRequest, setFileAnalyzeRequest] = useState(null);
  const [selectedStudyId, setSelectedStudyId] = useState(null);
  const [projects, setProjects] = useState([]);

  // Session 37 — Polling notice de suspension + handler logout auto
  const suspensionNotice = useSuspensionPoll();
  const handleSuspensionExpire = () => {
    localStorage.removeItem('oracle_token');
    localStorage.removeItem('oracle_user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setProjects([]);
  };
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [projectFormData, setProjectFormData] = useState({
  name: '',
  description: '',
  project_type: 'team',
  project_icon: '👥'
});
  const {
    conversations,
    currentConversationId,
    getCurrentConversation,
    addMessage,
    updateLastMessage
  } = useOracleStore();

  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [cmActive, setCmActive] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);

  // Chat général persistant
  const [generalConversations, setGeneralConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [generalForcedModel, setGeneralForcedModel] = useState(null);
  const [splitViewActive, setSplitViewActive] = useState(false);
  const [bookWorkspaceActive, setBookWorkspaceActive] = useState(false);
  const [autoOpenVoice, setAutoOpenVoice] = useState(false);
  const { previewContent, isPreviewOpen, showPreview, closePreview } = usePreviewPane();

  // ─── Greg welcome modal (Session Greg · 20 avril 2026) — affichée à chaque connexion ───
  const [gregWelcomeOpen, setGregWelcomeOpen] = useState(false);
  useEffect(() => {
    if (user?.username === 'greg') {
      setGregWelcomeOpen(true);
    }
  }, [user]);

  // ─── P1 force_password_change (25 avril 2026) — modale bloquante mdp temporaire ───
  const [forcePwdChangeOpen, setForcePwdChangeOpen] = useState(false);
  useEffect(() => {
    if (user?.force_password_change === true) setForcePwdChangeOpen(true);
    else setForcePwdChangeOpen(false);
  }, [user]);

  // ─── Landing ProfileDashboard pour users avec template (migration 053) ───
  // Si l'user a un profile_template_code et aucune page précise demandée via hash,
  // on l'envoie sur son dashboard dédié (template A pour Greg).
  useEffect(() => {
    if (user?.profile_template_code && !initialHash.page) {
      setCurrentPage('dashboard');
    }
  }, [user]);

  // S57 — Fetch feature flags au mount + pose sur window pour accès synchrone
  //   (pattern cohérent avec SpeechStudio / StudioImages qui refetch localement).
  //   Trigge un event pour que les composants qui dépendent du flag puissent réagir.
  useEffect(() => {
    const token = localStorage.getItem('oracle_token');
    if (!token) return;
    fetch('/api/capabilities/features', { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.features) {
          window.__itriiaFeatureFlags = d.features;
          window.dispatchEvent(new CustomEvent('itriia-features-ready', { detail: d.features }));
        }
      })
      .catch(() => {});
  }, []);

  // Sync hash URL ↔ état navigation
  useEffect(() => {
    if (selectedProject) {
      window.location.hash = `projects/${selectedProject.id}`;
    } else if (currentPage !== 'conversations') {
      window.location.hash = currentPage;
    } else {
      // Page par défaut — hash vide
      if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
    }
  }, [currentPage, selectedProject]);

  // Restaurer le projet depuis pendingProjectId après chargement des projets
  useEffect(() => {
    if (pendingProjectId && projects.length > 0 && !selectedProject) {
      const project = projects.find(p => p.id === pendingProjectId);
      if (project) {
        setSelectedProject(project);
        setExpandedMenu('projets');
      }
      setPendingProjectId(null);
    }
  }, [projects, pendingProjectId, selectedProject]);

  // Phase A (11 avril 2026) — filtrage sidebar par modules expertise retiré.
  // La future base d'expertise V2 apportera son propre mécanisme de visibilité.
  const { isDark } = useTheme();
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Authentification — vérifie le token au démarrage
  useEffect(() => {
    const token = localStorage.getItem('oracle_token');
    const userData = localStorage.getItem('oracle_user');

    if (token && userData) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Vérifier que le token est encore valide
      axios.get('/api/auth/me').then(res => {
        if (res.data && res.data.id) {
          setUser(res.data);
          localStorage.setItem('oracle_user', JSON.stringify(res.data));
          loadProjects();
          refreshQuota();
        } else {
          throw new Error('Invalid response');
        }
      }).catch(() => {
        // Token invalide → déconnexion propre
        localStorage.removeItem('oracle_token');
        localStorage.removeItem('oracle_user');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      });
    }
  }, []);

  // Chantier #5 — retour Stripe Checkout + ouverture pricing sur upsell
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const up = params.get('upgrade');
    if (up === 'success') {
      toast.success('Paiement confirmé — ton offre est en cours d\'activation.', 6000);
      setTimeout(refreshQuota, 1500);
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    } else if (up === 'cancel') {
      toast.info('Paiement annulé — aucun changement.');
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    }
    const onUpsell = () => { setCurrentPage('pricing'); setExpandedMenu(null); };
    window.addEventListener('itriia-upsell', onUpsell);
    return () => window.removeEventListener('itriia-upsell', onUpsell);
  }, [refreshQuota]);

  const loadProjects = async () => {
    try {
      const response = await axios.get('/api/projects');
      setProjects(response.data || []);
    } catch (error) {
      console.error('Erreur chargement projets:', error);
      setProjects([]);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);

    try {
      const response = await axios.post(`${API_URL}/auth/login`, loginData);

      if (response.data.token) {
        const { token, user } = response.data;
        localStorage.setItem('oracle_token', token);
        localStorage.setItem('oracle_user', JSON.stringify(user));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        // Purge du state password AVANT setUser : évite toute trace du mot
        // de passe dans le DOM via re-render intermédiaire (durcissement sécu).
        setLoginData({ username: '', password: '' });
        setUser(user);
        loadProjects();
        refreshQuota();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Erreur de connexion');
      // Purge le password en cas d'échec — l'user retape s'il veut réessayer.
      setLoginData(prev => ({ ...prev, password: '' }));
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleNavigation = (page) => {
    // Rôle formation (promo 1) : panneau Bases d'Expertise masqué. RÉVERSIBLE — retirer ce
    // guard (+ garde au rendu ~l.1114 + carte WelcomeScreen) pour réactiver l'accès le moment venu.
    if (isFormationPanel(user) && (page === 'expertise' || page === 'expertise-archives')) {
      setCurrentPage('conversations');
      return;
    }
    // S45 P1 : alias expertise-archives → page expertise avec flag scrollToArchives
    if (page === 'expertise-archives') {
      setCurrentPage('expertise');
      setExpertiseInitialView('archives');
      setExpandedMenu('expertise');
      setSelectedProject(null);
      return;
    }
    setExpertiseInitialView(null);
    setCurrentPage(page);
    // Garder le sous-menu ouvert si on navigue vers un de ses items
    const adminPages = ['admin-users', 'infrastructure', 'logs', 'comm-admin', 'dialecte-bench', 'data-memory-map', 'models-admin'];
    const analyticsPages = ['dashboard', 'metrics', 'costs'];
    const projectPages = ['projects', 'archives'];
    const expertisePages = ['expertise'];
    const rhPages = ['rh', 'rh-new', 'rh-graph', 'rh-detail'];
    if (adminPages.includes(page)) setExpandedMenu('admin');
    else if (analyticsPages.includes(page)) setExpandedMenu('analytics');
    else if (projectPages.includes(page)) setExpandedMenu('projets');
    else if (expertisePages.includes(page)) setExpandedMenu('expertise');
    else if (rhPages.includes(page)) setExpandedMenu('rh');
    else setExpandedMenu(null);
    setSelectedProject(null);
    if (!rhPages.includes(page)) setSelectedStudyId(null);
  };

 const handleCreateProject = () => {
  setShowCreateModal(true);
};
const handleSubmitProject = async (formData) => {
  try {
    const res = await axios.post('/api/projects', {
      name: formData.name,
      description: formData.description,
      project_type: formData.project_type,
      project_icon: formData.project_icon
    });
    setShowCreateModal(false);
    setProjectFormData({ name: '', description: '', project_type: 'team', project_icon: '👥' });
    await loadProjects();
    // Ouvrir directement le nouveau projet
    if (res.data?.project) {
      setSelectedProject(res.data.project);
    }
  } catch (error) {
    console.error('Erreur création projet:', error);
    toast.error('Erreur lors de la création du projet');
  }
};
  const handleToolAction = (tool) => () => {
    toast.info(`Outil ${tool} — en préparation`);
  };

  const handleAnalyze = () => toast.info('Fonction Analyser — en préparation');
  const handleSummary = () => toast.info('Fonction Résumé — en préparation');
  const handleTemplates = () => toast.info('Fonction Templates — en préparation');

  const handleClear = async () => {
    if (await confirmModal('Vider la conversation ? La mémoire projet sera préservée.')) {
      const currentConv = getCurrentConversation();
      currentConv.messages = [];
      toast.success('Conversation vidée, mémoire préservée');
    }
  };

  const handleBaseExpertise = () => toast.info('Fonction Base Expertise — en préparation');

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversations]);

  // Charger conversations générales + profil
  const loadGeneralConversations = async () => {
    try {
      const token = localStorage.getItem('oracle_token');
      if (!token) return;
      const [convRes, profileRes] = await Promise.all([
        axios.get('/api/chat/conversations', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/auth/profile-extracted', { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setGeneralConversations(convRes.data.conversations || []);
      setUserProfile(profileRes.data);
    } catch (err) {
      console.error('Erreur chargement conversations:', err);
    }
  };

  useEffect(() => {
    if (user) loadGeneralConversations();
  }, [user]);

  const handleNewConversation = async () => {
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await axios.post('/api/chat/conversations', {}, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setGeneralConversations(prev => [res.data, ...prev]);
      setActiveConvId(res.data.id);
    } catch (err) {
      console.error('Erreur création conversation:', err);
    }
  };

  // S50 — Nouveau chat privé (memory_scope='ephemeral')
  const handleNewPrivateConversation = async () => {
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await axios.post(
        '/api/chat/conversations',
        { memory_scope: 'ephemeral' },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setGeneralConversations(prev => [res.data, ...prev]);
      setActiveConvId(res.data.id);
      if (!localStorage.getItem('itriia_private_chat_toast_seen')) {
        try {
          const { toast } = await import('./components/ui/Toast');
          toast.info(
            "Chat privé activé. Cette conversation n'enrichira pas votre profil extrait et ne sera pas résumée. Rien ne fuitera vers d'autres sessions. Réversible via le cadenas du header.",
            { duration: 6000 }
          );
        } catch (_) {}
        localStorage.setItem('itriia_private_chat_toast_seen', '1');
      }
    } catch (err) {
      console.error('Erreur création conversation privée:', err);
    }
  };

  const handleStartCommMode = async () => {
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await axios.post('/api/chat/conversations', {
        title: '📡 Communication ITRIIA',
        mode: 'comm',
        comm_context: 'itriia'
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setGeneralConversations(prev => [res.data, ...prev]);
      setActiveConvId(res.data.id);
    } catch (err) {
      console.error('Erreur création conversation comm:', err);
    }
  };

  const handleDeleteConversation = async (id) => {
    try {
      const token = localStorage.getItem('oracle_token');
      await axios.delete(`/api/chat/conversations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneralConversations(prev => prev.filter(c => c.id !== id));
      if (activeConvId === id) setActiveConvId(null);
    } catch (err) {
      console.error('Erreur suppression:', err);
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };

    addMessage(userMessage);
    setMessage('');
    setIsLoading(true);

    const loadingMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      loading: true
    };

    addMessage(loadingMessage);

    try {
      const response = await axios.post('/conversation', {
        message: message,
        conversation_id: currentConversationId,
        enable_cm_model: cmActive
      });

      if (response.data.response) {
        updateLastMessage({
          content: response.data.response,
          loading: false
        });
      }
    } catch (error) {
      updateLastMessage({
        content: 'Erreur de communication avec ITRIIA: ' + error.message,
        loading: false,
        error: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!user) {
    return (
      <LoginPage
        loginData={loginData}
        setLoginData={setLoginData}
        onSubmit={handleLogin}
        isLoggingIn={isLoggingIn}
      />
    );
  }

  return (
<VaultZkProvider>
 <SuspensionNoticeOverlay notice={suspensionNotice} onExpire={handleSuspensionExpire} />
 <AppLayout
      user={user}
      onLogout={() => {
        localStorage.removeItem('oracle_token');
        localStorage.removeItem('oracle_user');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        setProjects([]);
      }}
      //showFilesSidebar={currentPage === 'conversations'}
     showFilesSidebar={selectedProject !== null && !splitViewActive && !focusMode && !bookWorkspaceActive}
     hideSidebar={splitViewActive}
     onNavigate={async (link) => {
       if (!link) return;
       // S50 — Suggestion de consolidation : navigue vers le projet + session ciblée
       const consolidateMatch = link.match(/^\/projects\/(\d+)#consolidate-(\d+)/);
       if (consolidateMatch) {
         const projectId = parseInt(consolidateMatch[1]);
         const project = projects.find(p => p.id === projectId);
         if (project) {
           setSelectedProject(project);
           setCurrentPage('projects');
           setExpandedMenu('projets');
           // hash conservé dans window.location pour permettre à ProjectDetail de cibler la session
           try { window.location.hash = `consolidate-${consolidateMatch[2]}`; } catch {}
         }
         return;
       }
       const expertiseMatch = link.match(/^\/expertise\/(\d+)/);
       if (expertiseMatch) {
         const baseId = parseInt(expertiseMatch[1]);
         // Trouver le projet attaché à cette base
         try {
           const resp = await axios.get(`/api/expertise-bases/${baseId}/project`);
           if (resp.data?.project_id) {
             const project = projects.find(p => p.id === resp.data.project_id);
             if (project) {
               setSelectedProject(project);
               setCurrentPage('projects');
               setExpandedMenu('projets');
               return;
             }
           }
         } catch { /* fallback liste projets */ }
         setSelectedProject(null);
         setCurrentPage('projects');
         return;
       }
       // Fallback : link est un nom de page simple (profile, conversations, etc.)
       // Appelé depuis UserKeyIndicator, NotificationBell, etc.
       handleNavigation(link);
     }}
    >
      {{
       sidebar: (
  <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
    {/* LOGO ITRIIA */}
    <div
      className="sidebar-logo"
      onClick={() => { handleNavigation('conversations'); setSelectedProject(null); }}
      title="Accueil"
      style={{
        padding: '20px 24px 16px 24px',
        borderBottom: '1px solid var(--sidebar-border)',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'opacity 0.2s'
      }}
      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.75'}
      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
    >
      <img
        src={isDark ? "/logo-itriia-white.png" : "/logo-itriia-dark.png"}
        alt="ITRIIA™ - Écosystème IA"
        style={{ height: '36px', objectFit: 'contain' }}
      />
    </div>

    <div className="sidebar-nav-container" style={{ padding: '0 8px', flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
              {/* Tableau de bord — uniquement pour les users avec template (Session Greg · migration 053) */}
              {user.profile_template_code && (
                <NavSection
                  icon={<Sparkles size={16} />}
                  title="Tableau de bord"
                  active={currentPage === 'dashboard'}
                  onClick={() => handleNavigation('dashboard')}
                  user={user}
                />
              )}

              <NavSection
                icon={<MessageSquare size={16} />}
                title="Mon Espace"
                active={currentPage === 'conversations'}
                onClick={() => handleNavigation('conversations')}
                user={user}
              />

              {!isFormationPanel(user) && (
                <NavSection
                  icon={<Headphones size={16} />}
                  title="ITRIIA Studio"
                  active={currentPage === 'speech'}
                  onClick={() => handleNavigation('speech')}
                  user={user}
                />
              )}

              {/* Session F — Coffre-fort utilisateur (cross-projet) */}
              {!isFormationPanel(user) && (
                <NavSection
                  icon={<Lock size={16} />}
                  title="Coffre-fort"
                  active={currentPage === 'vault'}
                  onClick={() => handleNavigation('vault')}
                  user={user}
                />
              )}

              {/* Scheduling — Entretiens / prise de RDV (Sabiloo) */}
              {!isFormationPanel(user) && (
                <NavSection
                  icon={<CalendarDays size={16} />}
                  title="Entretiens"
                  active={currentPage === 'booking'}
                  onClick={() => handleNavigation('booking')}
                  user={user}
                />
              )}

              {/* Session J — Mailing & Newsletter (listmonk) */}
              {!isFormationPanel(user) && (
                <NavSection
                  icon={<Mail size={16} />}
                  title="Mailing"
                  active={currentPage === 'mailing'}
                  onClick={() => handleNavigation('mailing')}
                  user={user}
                />
              )}

              {/* Module Documents — usine à conversion (OCR souverain) */}
              {!isFormationPanel(user) && (
                <NavSection
                  icon={<FileText size={16} />}
                  title="Documents"
                  active={currentPage === 'documents'}
                  onClick={() => handleNavigation('documents')}
                  user={user}
                />
              )}

              {/* Module ITRIIA DEV — cockpit développeur (admin + fondateur) */}
              {(user.role === 'admin_general' || user.role === 'oracle_fondateur') && (
                <NavSection
                  icon={<Terminal size={16} />}
                  title="ITRIIA DEV"
                  active={currentPage === 'dev-cockpit'}
                  onClick={() => handleNavigation('dev-cockpit')}
                  user={user}
                />
              )}

              {/* Chantier #5 — Abonnement / tarifs */}
              {!isFormationPanel(user) && (
                <NavSection
                  icon={<CreditCard size={16} />}
                  title="Mon abonnement"
                  active={currentPage === 'pricing'}
                  onClick={() => handleNavigation('pricing')}
                  user={user}
                />
              )}

              {/* Module LMS — Formations (apprenants + formateurs avec capability lms) */}
              {(user.role === 'admin_general' || user.permissions?.lms || user.permissions?.lms_instructor) && (
                <NavSection
                  icon={<GraduationCap size={16} />}
                  title="Formations"
                  active={currentPage === 'lms'}
                  onClick={() => handleNavigation('lms')}
                  user={user}
                />
              )}

              {/* Correctif rôle/permissions/cercle (mig 135/136) : le gate backend sur
                  /api/costs/dashboard|perf|credits exige permissions.analytics — le menu
                  doit refléter ça, sinon un lecteur clique et obtient un 403 silencieux. */}
              {!isFormationPanel(user) && (user.role === 'admin_general' || user.permissions?.analytics) && (
                <NavSection
                  icon={<BarChart3 size={16} />}
                  title="Analytics"
                  expanded={expandedMenu === 'analytics'}
                  onToggle={() => setExpandedMenu(expandedMenu === 'analytics' ? null : 'analytics')}
                  subItems={[
                    { label: 'Dashboard Personnel', path: 'dashboard', icon: <BarChart3 size={14} />, active: currentPage === 'dashboard' },
                    { label: 'Métriques Projet', path: 'metrics', icon: <TrendingUp size={14} /> },
                    { label: 'Coûts & Performance', path: 'costs', icon: <Wallet size={14} /> }
                  ]}
                  onClick={handleNavigation}
                  user={user}
                />
              )}

              <NavSection
                icon={<FolderOpen size={16} />}
                title="Projets"
                expanded={expandedMenu === 'projets'}
                onToggle={() => setExpandedMenu(expandedMenu === 'projets' ? null : 'projets')}
                subItems={[
                  { label: 'Mes Projets', path: 'projects', icon: <FolderOpen size={14} /> },
                  { label: 'Archives', path: 'archives', icon: <Archive size={14} /> }
                ]}
                onClick={handleNavigation}
                user={user}
              />

              {/* S44 Phase 3a — Historique hybride (visible uniquement quand l'user déplie volontairement Projets)
                  1. Top-5 "Dernières discussions" (tous projets) pour reprendre rapidement
                  2. Arbre dépliable "Mes projets" avec sessions groupées par date pour naviguer */}
              {expandedMenu === 'projets' && (() => {
                const openSession = (s) => {
                  const project = projects.find(p => p.id === s.project_id);
                  setPendingSessionId(s.id);
                  if (project) {
                    setSelectedProject(project);
                  } else {
                    setPendingProjectId(s.project_id);
                  }
                  setCurrentPage('projects');
                  setExpandedMenu('projets');
                };
                return (
                  <>
                    <RecentSessionsSubmenu onOpen={openSession} limit={5} />
                    <ProjectsTreeSubmenu
                      projects={projects}
                      currentProjectId={selectedProject?.id}
                      onOpen={openSession}
                    />
                  </>
                );
              })()}

              {/* Rendement Humain — réservé admin/fondateur ou collaborateurs avec permission explicite (Session Greg) */}
              {(user.role === 'admin_general' || user.role === 'oracle_fondateur' || user.permissions?.rendement_humain) && (
                <NavSection
                  icon={<Scale size={16} />}
                  title="Rendement Humain"
                  expanded={expandedMenu === 'rh'}
                  onToggle={() => setExpandedMenu(expandedMenu === 'rh' ? null : 'rh')}
                  subItems={[
                    { label: 'Études', path: 'rh', icon: <BarChart3 size={14} />, active: currentPage === 'rh' },
                    { label: 'Nouvelle étude', path: 'rh-new', icon: <Plus size={14} />, active: currentPage === 'rh-new' },
                    { label: 'Graphe causal', path: 'rh-graph', icon: <Share2 size={14} />, active: currentPage === 'rh-graph' }
                  ]}
                  onClick={handleNavigation}
                  user={user}
                />
              )}

              {/* Base Expertise V2 — module système sidebar (S45 P1 : dépliable avec dernières bases créées)
                  Session Greg NEXT — chantier C.1 : accessible aussi aux collaborateurs ayant permissions.bases_expertise (lecture système + leurs propres bases, quota 3) */}
              {(user.role === 'admin_general' || user.role === 'oracle_fondateur' || user.permissions?.bases_expertise) && (
                <>
                  <NavSection
                    icon={<Database size={16} />}
                    title="Bases d'Expertise"
                    expanded={expandedMenu === 'expertise'}
                    onToggle={() => setExpandedMenu(expandedMenu === 'expertise' ? null : 'expertise')}
                    subItems={[
                      { label: 'Toutes les bases', path: 'expertise', icon: <Database size={14} />, active: currentPage === 'expertise' },
                      { label: 'Archivées', path: 'expertise-archives', icon: <Archive size={14} /> }
                    ]}
                    onClick={handleNavigation}
                    user={user}
                  />
                  {expandedMenu === 'expertise' && (
                    <BasesSubmenu
                      onOpen={(b) => {
                        setPendingBaseId(b.id);
                        setExpertiseInitialView(null);
                        setCurrentPage('expertise');
                        setSelectedProject(null);
                      }}
                      limit={5}
                    />
                  )}
                </>
              )}

              {/* Fondateur (Pierre) : accès direct à la carte, vue curée — PAS la section Administration */}
              {user.role === 'oracle_fondateur' && (
                <NavSection
                  icon={<Network size={16} />}
                  title="Data & Memory Map"
                  active={currentPage === 'data-memory-map'}
                  onClick={() => handleNavigation('data-memory-map')}
                  user={user}
                />
              )}

              {user.role === 'admin_general' && (
                <NavSection
                  icon={<Shield size={16} />}
                  title="Administration"
                  expanded={expandedMenu === 'admin'}
                  onToggle={() => setExpandedMenu(expandedMenu === 'admin' ? null : 'admin')}
                  subItems={[
                    { label: 'Utilisateurs', path: 'admin-users', icon: <Users size={14} /> },
                    { label: 'Communication KB', path: 'comm-admin', icon: <MessageSquare size={14} /> },
                    { label: 'Infrastructure', path: 'infrastructure', icon: <Server size={14} /> },
                    { label: 'Data & Memory Map', path: 'data-memory-map', icon: <Network size={14} /> },
                    { label: 'Logs', path: 'logs', icon: <FileText size={14} /> },
                    { label: 'BENCH Dialecte', path: 'dialecte-bench', icon: <Wand2 size={14} /> },
                    { label: 'Modèles', path: 'models-admin', icon: <Brain size={14} /> },
                    { label: 'Chroniques', path: 'chronicles-admin', icon: <ScrollText size={14} /> },
                  ]}
                  onClick={handleNavigation}
                  user={user}
                />
              )}
            </div>

            {/* Chroniques — fil d'actualité ITRIIA partagé pour tous les users */}
            <SidebarChronicles limit={5} />

            {/* Mon Profil */}
            <div style={{ padding: '8px 16px', borderTop: '1px solid var(--sidebar-border)' }}>
              <button
                className="sidebar-profile-btn"
                title="Mon profil"
                onClick={() => handleNavigation('profile')}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: currentPage === 'profile' ? 'var(--accent)' : 'transparent',
                  color: currentPage === 'profile' ? '#fff' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: currentPage === 'profile' ? '600' : '400',
                  transition: 'all 0.2s'
                }}
              >
                <User size={16} style={{ flexShrink: 0 }} /> <span>Mon profil</span>
              </button>
            </div>
          </div>
        ),

        toggleCM: (
          <ToggleCM cmActive={cmActive} onToggle={() => setCmActive(!cmActive)} />
        ),

        main: (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: ['rh', 'profile', 'archives', 'admin-users', 'comm-admin', 'metrics', 'costs', 'dashboard', 'expertise', 'infrastructure', 'logs', 'data-memory-map', 'models-admin', 'dev-cockpit'].some(p => currentPage.startsWith(p)) ? 'auto' : 'hidden', height: '100%' }}>
            {/* Oracle Conversations — Chat persistant */}
            {currentPage === 'conversations' && (
              <div style={{ display: 'flex', height: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <ConversationList
                    conversations={generalConversations}
                    activeId={activeConvId}
                    onSelect={setActiveConvId}
                    onNew={handleNewConversation}
                    onNewPrivate={handleNewPrivateConversation}
                    onDelete={handleDeleteConversation}
                  />
                  <ProfileIndicator
                    score={userProfile?.completeness_score}
                    onClick={() => setShowProfileModal(true)}
                  />
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {activeConvId ? (
                    <SplitChatView
                      mode="auto"
                      isOpen={isPreviewOpen}
                      onToggle={() => { closePreview(); setSplitViewActive(false); }}
                      onFullScreenChange={(fs) => setSplitViewActive(fs)}
                      previewContent={previewContent}
                    >
                      <ConversationChat
                        conversationId={activeConvId}
                        cmActive={cmActive}
                        user={user}
                        forcedModel={generalForcedModel}
                        onForcedModelChange={setGeneralForcedModel}
                        conversationMode={generalConversations.find(c => c.id === activeConvId)?.mode || 'general'}
                        onPreview={showPreview}
                        autoOpenVoice={autoOpenVoice}
                      />
                    </SplitChatView>
                  ) : (
                    <WelcomeScreen
                      user={user}
                      profile={userProfile}
                      onStartConversation={handleNewConversation}
                      onStartCommMode={handleStartCommMode}
                      onStartVoice={() => {
                        setAutoOpenVoice(true);
                        handleNewConversation();
                      }}
                      onSuggestion={(text) => {
                        handleNewConversation().then(() => {
                          // Le message sera tapé manuellement après création
                        });
                      }}
                      onNavigateExpertise={() => handleNavigation('expertise')}
                    />
                  )}
                </div>
                {showProfileModal && (
                  <ProfileSummary
                    onClose={() => setShowProfileModal(false)}
                    onUpdate={loadGeneralConversations}
                  />
                )}
              </div>
            )}

            {/* Dashboards — priorité au ProfileDashboard si l'user a un template assigné (migration 053) */}
            {currentPage === 'dashboard' && user.profile_template_code && (
              <ProfileDashboard
                templateCode={user.profile_template_code}
                user={user}
                onProjectSelect={(project) => { setSelectedProject(project); setCurrentPage('projects'); }}
              />
            )}
            {currentPage === 'dashboard' && !user.profile_template_code && user.role === 'admin_general' && <BenDashboard onProjectSelect={(project) => { setSelectedProject(project); setCurrentPage('projects'); }} />}
            {currentPage === 'dashboard' && !user.profile_template_code && user.role === 'oracle_fondateur' && <PierreDashboard onProjectSelect={(project) => { setSelectedProject(project); setCurrentPage('projects'); }} />}
            {currentPage === 'dashboard' && !user.profile_template_code && user.role !== 'admin_general' && user.role !== 'oracle_fondateur' && <AnnieDashboard onProjectSelect={(project) => { setSelectedProject(project); setCurrentPage('projects'); }} />}

          
            {/* Page Projets - Liste */}
            {currentPage === 'projects' && !selectedProject && (
              <ProjectsList 
                onProjectSelect={(projectId) => {
                  const project = projects.find(p => p.id === projectId);
                  setSelectedProject(project);
                }}
                onCreateProject={handleCreateProject}
                onLoadProjects={loadProjects}
                projects={projects}
              />
            )}

            {/* Page Projets - Détail avec ProjectDetail Component */}
            {currentPage === 'projects' && selectedProject && (
              <ProjectDetail
                projectId={selectedProject.id}
                onBack={() => { setSelectedProject(null); setFocusMode(false); loadProjects(); }}
                user={user}
                cmActive={cmActive}
                onFilesChanged={() => setFilesRefreshKey(k => k + 1)}
                fileAnalyzeRequest={fileAnalyzeRequest}
                onProjectUpdated={(updated) => {
                  setSelectedProject(prev => ({ ...prev, ...updated }));
                  setProjects(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
                }}
                onSplitViewChange={setSplitViewActive}
                onBookWorkspaceChange={setBookWorkspaceActive}
                isFullScreen={splitViewActive}
                initialSessionId={pendingSessionId}
                onInitialSessionConsumed={() => setPendingSessionId(null)}
                focusMode={focusMode}
                onFocusToggle={setFocusMode}
                projects={projects}
              />
            )}

            {/* Page Administration Utilisateurs */}
            {currentPage === 'admin-users' && user.role === 'admin_general' && <UserManagement />}
            {currentPage === 'models-admin' && user.role === 'admin_general' && <ModelCatalogAdmin />}
            {currentPage === 'comm-admin' && user.role === 'admin_general' && <CommKnowledgeAdmin />}

            {/* Page Archives */}
            {currentPage === 'archives' && <ProjectArchives onLoadProjects={loadProjects} />}

            {/* Page Mon Profil */}
            {currentPage === 'profile' && <UserProfile />}

            {/* Chantier #5 — Page abonnement / tarifs */}
            {currentPage === 'pricing' && <PricingPage quota={quota} onRefresh={refreshQuota} />}

            {/* Session F — Page Coffre-fort utilisateur (verrouillée pour le freemium) */}
            {currentPage === 'vault' && (
              quota && !quota.features.zk
                ? <FeatureLock feature="zk" onUpgrade={() => handleNavigation('pricing')} />
                : <VaultPage />
            )}

            {/* Scheduling — Entretiens HITL */}
            {currentPage === 'booking' && <BookingPage />}

            {/* Session J — Page Mailing & Newsletter */}
            {currentPage === 'mailing' && <MailingPage user={user} />}

            {/* Module Documents — usine à conversion */}
            {currentPage === 'documents' && (
              quota && !quota.features.documents
                ? <FeatureLock feature="documents" onUpgrade={() => handleNavigation('pricing')} />
                : <DocumentsPage />
            )}

            {/* Module ITRIIA DEV — cockpit développeur */}
            {currentPage === 'dev-cockpit' && <DevCockpitPage />}

            {/* Rendement Humain - Dashboard */}
            {currentPage === 'rh' && !selectedStudyId && (
              <RHDashboard
                onNavigate={handleNavigation}
                onStudySelect={(id) => { setSelectedStudyId(id); setCurrentPage('rh-detail'); setExpandedMenu('rh'); }}
              />
            )}

            {/* Rendement Humain - Détail étude */}
            {currentPage === 'rh-detail' && selectedStudyId && (
              <RHStudyDetail
                studyId={selectedStudyId}
                onNavigate={(page) => { setSelectedStudyId(null); handleNavigation(page); }}
              />
            )}

            {/* Rendement Humain - Nouvelle étude */}
            {currentPage === 'rh-new' && (
              <RHStudyForm
                onNavigate={handleNavigation}
                onStudyCreated={(id) => { setSelectedStudyId(id); setCurrentPage('rh-detail'); setExpandedMenu('rh'); }}
              />
            )}

            {/* Rendement Humain - Graphe causal */}
            {currentPage === 'rh-graph' && (
              <RHCausalGraph onNavigate={(page, id) => {
                if (page === 'rh-detail' && id) { setSelectedStudyId(id); setCurrentPage('rh-detail'); setExpandedMenu('rh'); }
                else handleNavigation(page);
              }} />
            )}

            {/* Dashboard Coûts */}
            {currentPage === 'costs' && <CostDashboard />}

            {/* Métriques Projet — Session 32 */}
            {currentPage === 'metrics' && (
              <ProjectMetrics onProjectSelect={(project) => { setSelectedProject(project); setCurrentPage('projects'); }} />
            )}

            {/* Admin Infrastructure — Session 32 */}
            {currentPage === 'infrastructure' && user.role === 'admin_general' && <InfrastructureDashboard />}

            {/* Data & Memory Map — admin_general (complet) ou oracle_fondateur (vue curée : Provenance/Performance/Flux) */}
            {currentPage === 'data-memory-map' && (user.role === 'admin_general' || user.role === 'oracle_fondateur') && <DataMemoryMap restricted={user.role === 'oracle_fondateur'} />}

            {/* Admin Logs — Session 32 */}
            {currentPage === 'logs' && user.role === 'admin_general' && <LogsDashboard />}

            {/* Admin BENCH Dialecte — Session 55 P4 */}
            {currentPage === 'dialecte-bench' && user.role === 'admin_general' && <DialecteBench />}
            {currentPage === 'chronicles-admin' && user.role === 'admin_general' && <ChroniclesAdmin />}

            {/* ITRIIA Studio — Speech (verrouillé pour le freemium) */}
            {currentPage === 'speech' && (
              quota && !quota.features.studio
                ? <FeatureLock feature="studio" onUpgrade={() => handleNavigation('pricing')} />
                : <SpeechStudio user={user} />
            )}

            {/* Bases d'Expertise V2 — masquée pour le rôle formation (promo 1, réversible).
                Correctif rôle/permissions/cercle (mig 135/136) : le backend gate désormais
                tout /api/expertise-bases par permissions.bases_expertise (sauf lecture des
                attachements projet) — le rendu de page doit revérifier cette permission,
                pas seulement le lien de menu (qui la vérifiait déjà, cf. ligne ~863). */}
            {currentPage === 'expertise' && !isFormationPanel(user) && (user.role === 'admin_general' || user.role === 'oracle_fondateur' || user.permissions?.bases_expertise) && (
              <div style={{ height: '100%', overflow: 'auto' }}>
                <ExpertiseBasesPanel
                  user={user}
                  initialBaseId={pendingBaseId}
                  initialView={expertiseInitialView}
                  onConsumeInitial={() => { setPendingBaseId(null); setExpertiseInitialView(null); }}
                />
              </div>
            )}

            {currentPage === 'lms' && (
              <div style={{ height: '100%', overflow: 'hidden' }}>
                <LmsPanel user={user} />
              </div>
            )}
          </div>
        ),

        // Files Sidebar droite - SEULEMENT pour projets
       // Files Sidebar droite - SEULEMENT pour projets

filesSidebar: (
  <FilesSidebar
    projectId={selectedProject?.id}
    user={user}
    refreshKey={filesRefreshKey}
    onFileAnalyze={(fileId, fileName, mode) => setFileAnalyzeRequest({ fileId, fileName, mode, ts: Date.now() })}
    pasteWidget={
      <PasteAsFileWidget
        projectId={selectedProject?.id}
        onFileSaved={(fileInfo) => {
          console.log('Fichier sauvegardé:', fileInfo);
          setFilesRefreshKey(k => k + 1);
        }}
      />
    }
  />
)      }}      
    </AppLayout>
{/* Modal Création Projet — Premium */}
{showCreateModal && (
  <div
    onClick={() => {
      setShowCreateModal(false);
      setProjectFormData({ name: '', description: '', project_type: 'team', project_icon: '👥' });
    }}
    style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(8, 12, 28, 0.62)',
      backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, animation: 'fadeIn 0.18s ease-out'
    }}
  >
    <style>{`
      @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      @keyframes scaleIn { from { opacity: 0; transform: translateY(8px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      .itriia-create-modal input::placeholder, .itriia-create-modal textarea::placeholder { color: var(--text-muted, #6a6a85); opacity: 0.7; }
      .itriia-type-card:hover { transform: translateY(-1px); }
    `}</style>

    <div
      onClick={e => e.stopPropagation()}
      className="itriia-create-modal"
      style={{
        backgroundColor: 'var(--modal-bg)',
        border: '1px solid var(--modal-border)',
        borderRadius: '16px',
        padding: '0',
        width: '580px',
        maxWidth: '92%',
        boxShadow: '0 24px 60px rgba(0,0,0,0.45), 0 0 0 1px rgba(59,130,246,0.08), 0 0 60px rgba(29,78,216,0.08)',
        animation: 'scaleIn 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '22px 28px 18px',
        borderBottom: '1px solid var(--card-border)',
        background: 'linear-gradient(135deg, rgba(29,78,216,0.06) 0%, rgba(59,130,246,0.02) 100%)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #1D4ED8 0%, #3b82f6 100%)',
            boxShadow: '0 4px 14px rgba(29,78,216,0.35)'
          }}>
            <Sparkles size={19} color="#fff" strokeWidth={2.2} />
          </div>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)', margin: 0, letterSpacing: '-0.2px' }}>
              Nouveau projet ITRIIA
            </h2>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: 2 }}>
              Un espace dédié avec mémoire continue et chat IA
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setShowCreateModal(false);
            setProjectFormData({ name: '', description: '', project_type: 'team', project_icon: '👥' });
          }}
          style={{
            width: 32, height: 32, borderRadius: 8,
            border: '1px solid var(--card-border)', background: 'transparent',
            color: 'var(--text-secondary)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, lineHeight: 1, transition: 'all 0.15s'
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--card-bg)'; e.currentTarget.style.color = 'var(--text)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          aria-label="Fermer"
        >×</button>
      </div>

      {/* Body form */}
      <form onSubmit={(e) => {
        e.preventDefault();
        handleSubmitProject(projectFormData);
      }} style={{ padding: '24px 28px 22px' }}>

        {/* Nom + Description : layout 2 colonnes */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '7px', fontWeight: 600, color: 'var(--text)', fontSize: 12, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
            Nom du projet <span style={{ color: '#ef4444', fontWeight: 500 }}>*</span>
          </label>
          <input
            type="text"
            value={projectFormData.name}
            onChange={(e) => setProjectFormData(prev => ({ ...prev, name: e.target.value }))}
            autoFocus
            placeholder="Ex : Module RH v2, Dossier Acme Corp, Étude marché Q2…"
            style={{
              width: '100%', padding: '13px 16px',
              border: '1px solid var(--input-border)', borderRadius: '10px',
              fontSize: '14px', color: 'var(--text)',
              backgroundColor: 'var(--input-bg)', outline: 'none',
              transition: 'border-color 0.2s, box-shadow 0.2s', boxSizing: 'border-box'
            }}
            onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.15)'; }}
            onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.boxShadow = 'none'; }}
            required
            maxLength={120}
          />
        </div>

        <div style={{ marginBottom: '22px' }}>
          <label style={{ display: 'block', marginBottom: '7px', fontWeight: 600, color: 'var(--text)', fontSize: 12, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
            Description <span style={{ color: 'var(--text-secondary)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>· optionnel</span>
          </label>
          <textarea
            value={projectFormData.description}
            onChange={(e) => setProjectFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Contexte, objectifs, points d'attention pour l'IA…"
            style={{
              width: '100%', padding: '13px 16px',
              border: '1px solid var(--input-border)', borderRadius: '10px',
              fontSize: '14px', minHeight: '72px', resize: 'vertical',
              color: 'var(--text)', backgroundColor: 'var(--input-bg)', outline: 'none',
              transition: 'border-color 0.2s, box-shadow 0.2s', boxSizing: 'border-box',
              fontFamily: 'inherit', lineHeight: 1.5
            }}
            onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.15)'; }}
            onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.boxShadow = 'none'; }}
            maxLength={500}
          />
        </div>

        {/* Type de projet */}
        <div style={{ marginBottom: '26px' }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 600, color: 'var(--text)', fontSize: 12, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
            Type de projet
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
            {[
              { id: 'development', label: 'Développement', icon: '🚀', color: '#3b82f6', desc: 'Roadmap, code, specs' },
              { id: 'expertise',   label: 'Expertise',     icon: '🧠', color: '#8b5cf6', desc: 'Savoir structuré' },
              { id: 'commercial',  label: 'Commercial',    icon: '💼', color: '#10b981', desc: 'Deals, clients' },
              { id: 'team',        label: 'Équipe',        icon: '👥', color: '#f59e0b', desc: 'Collaboration' },
              { id: 'research',    label: 'Recherche',     icon: '🔬', color: '#06b6d4', desc: 'Études, analyses' },
              { id: 'documentation', label: 'Documentation', icon: '📚', color: '#ec4899', desc: 'Guides, rapports' }
            ].map(type => {
              const selected = projectFormData.project_type === type.id;
              return (
                <div
                  key={type.id}
                  className="itriia-type-card"
                  onClick={() => setProjectFormData(prev => ({
                    ...prev, project_type: type.id, project_icon: type.icon
                  }))}
                  style={{
                    padding: '12px 10px',
                    border: `1.5px solid ${selected ? type.color : 'var(--card-border)'}`,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    backgroundColor: selected ? `${type.color}14` : 'var(--card-bg)',
                    boxShadow: selected ? `0 0 0 3px ${type.color}20, 0 4px 12px ${type.color}18` : 'none',
                    transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative'
                  }}
                >
                  {selected && (
                    <div style={{
                      position: 'absolute', top: 6, right: 6,
                      width: 14, height: 14, borderRadius: '50%',
                      background: type.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 9, fontWeight: 700, lineHeight: 1
                    }}>✓</div>
                  )}
                  <div style={{ fontSize: '22px', marginBottom: '4px', filter: selected ? 'none' : 'saturate(0.85)' }}>{type.icon}</div>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{type.label}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)', lineHeight: 1.3 }}>{type.desc}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer actions */}
        <div style={{
          display: 'flex', gap: '10px', justifyContent: 'flex-end',
          paddingTop: '16px', borderTop: '1px solid var(--card-border)'
        }}>
          <button
            type="button"
            onClick={() => {
              setShowCreateModal(false);
              setProjectFormData({ name: '', description: '', project_type: 'team', project_icon: '👥' });
            }}
            style={{
              padding: '11px 18px',
              border: '1px solid var(--card-border)', borderRadius: '10px',
              backgroundColor: 'transparent', color: 'var(--text-secondary)',
              cursor: 'pointer', fontSize: 14, fontWeight: 500,
              transition: 'all 0.15s'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--card-bg)'; e.currentTarget.style.color = 'var(--text)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={!projectFormData.name.trim()}
            style={{
              padding: '11px 22px',
              border: 'none', borderRadius: '10px',
              background: projectFormData.name.trim()
                ? 'linear-gradient(135deg, #1D4ED8 0%, #3b82f6 100%)'
                : 'var(--card-border)',
              color: '#ffffff', cursor: projectFormData.name.trim() ? 'pointer' : 'not-allowed',
              fontWeight: 600, fontSize: 14,
              display: 'flex', alignItems: 'center', gap: 8,
              boxShadow: projectFormData.name.trim()
                ? '0 6px 18px rgba(29,78,216,0.38), 0 0 0 1px rgba(59,130,246,0.15)'
                : 'none',
              opacity: projectFormData.name.trim() ? 1 : 0.55,
              transition: 'all 0.18s'
            }}
          >
            <Sparkles size={15} strokeWidth={2.4} />
            Créer le projet
          </button>
        </div>
      </form>
    </div>
  </div>
)}
<WelcomeGregModal
  open={gregWelcomeOpen}
  onClose={() => setGregWelcomeOpen(false)}
/>
<ForcePasswordChangeModal
  open={forcePwdChangeOpen}
  onSuccess={() => {
    // Backend a flippé force_password_change → reflet local immédiat
    const updated = { ...user, force_password_change: false };
    setUser(updated);
    try { localStorage.setItem('oracle_user', JSON.stringify(updated)); } catch { /* silent */ }
    setForcePwdChangeOpen(false);
  }}
/>
<ToastContainer />
</VaultZkProvider>
 );
}

export default App;
